/**
 * /try Media Telemetry Event Builder.
 *
 * WHAT THIS FILE DOES:
 * - Defines stable event names for /try media client-transcode observability.
 * - Builds analytics payloads for attempt/success/fallback + byte reduction.
 * - Keeps telemetry formatting logic out of try-ui.ts and try-speculation.ts.
 *
 * WHY THIS EXISTS:
 * - Phase 4 requires media telemetry from both submit and speculation paths.
 * - Centralizing event shape avoids drift and copy/paste bugs.
 *
 * CALLED BY:
 * - src/client/try-ui.ts during media submit flow
 * - src/client/try-speculation.ts during speculative media staging
 *
 * @file src/client/try-media-telemetry.ts
 */

import {
  TryMediaTranscodeFallbackReasons,
  type TryAnalyticsEventInput,
  type TryMediaClientTranscodeTelemetry
} from './try-shared.js';

/**
 * Stable analytics event names for /try media client-first ingest.
 */
export const TryMediaTelemetryEvents = Object.freeze({
  CLIENT_TRANSCODE_ATTEMPT: 'try.media.client_transcode.attempt',
  CLIENT_TRANSCODE_SUCCESS: 'try.media.client_transcode.success',
  CLIENT_TRANSCODE_FALLBACK: 'try.media.client_transcode.fallback',
  UPLOAD_BYTES_REDUCTION_RATIO: 'try.media.upload.bytes_reduction_ratio'
} as const);

/**
 * Surface labels for media telemetry event attribution.
 */
export const TryMediaTelemetrySources = Object.freeze({
  UI_SUBMIT: 'try_ui_submit',
  SPECULATION: 'try_ui_speculation'
} as const);

export type TryMediaTelemetrySource =
  typeof TryMediaTelemetrySources[keyof typeof TryMediaTelemetrySources];

/**
 * Builds the analytics event batch for one media transcode routing decision.
 */
export function buildTryMediaTelemetryEvents(params: {
  source: TryMediaTelemetrySource;
  telemetry: TryMediaClientTranscodeTelemetry;
}): TryAnalyticsEventInput[] {
  const metadataBase = {
    source: params.source,
    mediaKind: params.telemetry.mediaKind,
    usedClientTranscode: params.telemetry.usedClientTranscode,
    originalContentType: params.telemetry.originalContentType,
    uploadedContentType: params.telemetry.uploadedContentType,
    originalBytes: params.telemetry.originalBytes,
    uploadedBytes: params.telemetry.uploadedBytes,
    bytesSaved: params.telemetry.bytesSaved,
    reductionRatio: roundTo(params.telemetry.reductionRatio, 4)
  };

  const events: TryAnalyticsEventInput[] = [
    {
      event: TryMediaTelemetryEvents.CLIENT_TRANSCODE_ATTEMPT,
      metadata: metadataBase
    }
  ];

  if (params.telemetry.usedClientTranscode) {
    events.push({
      event: TryMediaTelemetryEvents.CLIENT_TRANSCODE_SUCCESS,
      metadata: metadataBase
    });
  } else {
    events.push({
      event: TryMediaTelemetryEvents.CLIENT_TRANSCODE_FALLBACK,
      metadata: {
        ...metadataBase,
        fallbackReason: params.telemetry.fallbackReason ?? TryMediaTranscodeFallbackReasons.UNKNOWN
      }
    });
  }

  events.push({
    event: TryMediaTelemetryEvents.UPLOAD_BYTES_REDUCTION_RATIO,
    metadata: {
      ...metadataBase,
      reductionPercent: roundTo(params.telemetry.reductionRatio * 100, 2)
    }
  });

  return events;
}

/**
 * Rounds finite values to a fixed decimal precision.
 */
function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  const scale = 10 ** decimals;
  return Math.round(value * scale) / scale;
}
